import type { MouseEvent, ReactElement } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import Chip from '@mui/material/Chip';
import {
    endsTextSelection,
    opensOutsideTheApp,
    repeatsClick,
    SELECTABLE_LINK_SX,
    stopDoubleClick
} from './routerLink';

/**
 * A record named as a chip that links to it — a Projekt in the Auswertung, the
 * Funde a Probe became, the Probe a Fund came from. The leading icon says what
 * kind of record it is and flags the chip as a link.
 *
 * A real `<a href>`, so it can be middle-clicked into a new tab (see
 * components/routerLink). A plain click stays in the app and never reaches the
 * grid row or card around it.
 *
 * Without `to` it is the same chip, not linking — for a record this app cannot
 * open from where it stands (the field tool has no Fund editor).
 */
export default function RecordLinkChip({
    label,
    to,
    icon,
    onOpen
}: {
    label: string;
    to?: string;
    icon: ReactElement;
    /**
     * Navigate handler — inside an editor, its GUARDED navigate. Omitted, the
     * chip navigates directly, which is right outside an editor.
     */
    onOpen?: (to: string) => void;
}) {
    const navigate = useNavigate();
    if (!to) {
        return (
            <Chip size="small" variant="outlined" icon={icon} label={label} />
        );
    }
    const handleClick = (event: MouseEvent) => {
        event.stopPropagation();
        if (opensOutsideTheApp(event)) {
            return;
        }
        event.preventDefault();
        if (repeatsClick(event) || endsTextSelection(event)) {
            return;
        }
        if (onOpen) {
            onOpen(to);
        } else {
            navigate(to);
        }
    };
    return (
        <Chip
            size="small"
            variant="outlined"
            icon={icon}
            label={label}
            component={RouterLink}
            to={to}
            draggable={false}
            sx={SELECTABLE_LINK_SX}
            clickable
            onClick={handleClick}
            onDoubleClick={stopDoubleClick}
        />
    );
}
